import { useEffect, useState } from "react";
import { api, apiError, imgUrl } from "../lib/api";
import { StudioShell } from "../components/StudioShell";
import { UploadZone } from "../components/UploadZone";
import { StyleSelect } from "../components/StyleSelect";
import { toast } from "sonner";

const TYPES = ["all", "fabric", "print", "colorway"];
const MAT_SOURCES = [
  { key: "all", label: "All" },
  { key: "material", label: "Ad-Hoc" },
  { key: "plm", label: "Libraries" },
];

export default function MaterialStudio() {
  const [designs, setDesigns] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [filter, setFilter] = useState("all");
  const [matSource, setMatSource] = useState("all");
  const [source, setSource] = useState("design"); // design | upload
  const [designId, setDesignId] = useState("");
  const [garment, setGarment] = useState(null);
  const [materialId, setMaterialId] = useState("");
  const [instructions, setInstructions] = useState("");
  const [styleId, setStyleId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, []);

  const filtered = materials.filter((m) => {
    if (filter !== "all" && m.type !== filter) return false;
    if (matSource !== "all" && (m.source || "material") !== matSource) return false;
    return true;
  });
  const canGenerate = materialId && (source === "design" ? designId : garment);

  const generate = async () => {
    setGenerating(true);
    try {
      const body = { material_id: materialId, instructions, style_id: styleId || null };
      if (source === "design") body.design_id = designId; else body.garment_b64 = garment;
      const { data } = await api.post("/generate/material", body);
      setResult(data);
      toast.success("Material applied");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <StudioShell title="Apply Materials & Prints" subtitle="Change fabrics, prints and colorways on any garment"
      onGenerate={generate} generating={generating} disabled={!canGenerate} result={result} generateLabel="Apply material">
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Garment source</label>
        <div className="mt-2 flex gap-2">
          <button data-testid="material-source-design" onClick={() => setSource("design")}
            className={`px-4 py-2 rounded-md text-sm border transition-colors ${source === "design" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>From my designs</button>
          <button data-testid="material-source-upload" onClick={() => setSource("upload")}
            className={`px-4 py-2 rounded-md text-sm border transition-colors ${source === "upload" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>Upload image</button>
        </div>
      </div>
      {source === "design" ? (
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Select design</label>
          <div className="mt-2 grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
            {designs.length === 0 && <p className="text-xs text-zinc-400 col-span-3">No designs yet — create one in Sketch to Render.</p>}
            {designs.map((d) => (
              <button key={d.id} data-testid={`material-design-${d.id}`} onClick={() => setDesignId(d.id)}
                className={`border rounded-md overflow-hidden text-left transition-colors ${designId === d.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
                <img src={imgUrl(d.cover_image)} alt={d.title} className="w-full h-20 object-cover" />
                <p className="text-[10px] p-1.5 truncate">{d.title}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <UploadZone value={garment} onChange={setGarment} label="Upload garment image" testId="material-garment-upload" />
      )}
      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Material</label>
          <div className="flex gap-1">
            {TYPES.map((t) => (
              <button key={t} data-testid={`material-filter-${t}`} onClick={() => setFilter(t)}
                className={`px-2.5 py-1 rounded text-[11px] capitalize transition-colors ${filter === t ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Source</span>
          {MAT_SOURCES.map((s) => (
            <button key={s.key} data-testid={`material-source-${s.key}`} onClick={() => setMatSource(s.key)}
              className={`px-2 py-0.5 rounded text-[10px] transition-colors ${matSource === s.key ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100 border border-zinc-200"}`}>{s.label}</button>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
          {filtered.length === 0 && matSource === "plm" && (
            <p className="col-span-4 text-[11px] text-zinc-500 border border-dashed border-zinc-200 rounded-md p-3">
              No PLM Library items match — add a <a href="/libraries" className="underline decoration-dotted hover:text-zinc-950">Fabric or Print with an artwork/swatch</a> in Libraries and it will show up here automatically.
            </p>
          )}
          {filtered.length === 0 && matSource !== "plm" && (
            <p className="col-span-4 text-[11px] text-zinc-500 border border-dashed border-zinc-200 rounded-md p-3">
              No materials yet. Upload one under <a href="/library" className="underline decoration-dotted hover:text-zinc-950">Ad-Hoc Materials</a> or add a Fabric/Print with a swatch in Libraries.
            </p>
          )}
          {filtered.map((m) => (
            <button key={m.id} data-testid={`material-swatch-${m.id}`} onClick={() => setMaterialId(m.id)} title={`${m.name}${m.source === "plm" ? " · PLM Library" : " · Ad-Hoc"}`}
              className={`relative border rounded-md overflow-hidden transition-colors ${materialId === m.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
              {m.source === "plm" && (
                <span className="absolute top-1 left-1 bg-zinc-950 text-white text-[8px] uppercase tracking-[0.1em] font-medium px-1 py-0.5 rounded z-10">PLM</span>
              )}
              <img src={imgUrl(m.image_url)} alt={m.name} className="w-full h-16 object-cover" />
              <p className="text-[10px] p-1 truncate">{m.name}</p>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-zinc-400 mt-1.5">Ad-Hoc uploads and PLM Libraries (fabrics & prints with an artwork) both appear here — items with a PLM badge come from your governed master data.</p>
      </div>
      <StyleSelect value={styleId} onChange={setStyleId} />
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Instructions (optional)</label>
        <textarea data-testid="material-instructions-input" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2}
          placeholder="e.g. apply only to the sleeves, keep buttons black"
          className="mt-2 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
      </div>
    </StudioShell>
  );
}
